import { ReactNode, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  FileText,
  Briefcase,
  BarChart3,
  Building2,
  Award,
  Landmark,
  LogOut,
  User,
  ChevronsLeft,
  ChevronsRight,
  Sun,
  Moon,
  Monitor,
} from 'lucide-react'
import { useAuthContext } from '../../../features/auth'
import { useTheme } from '../../hooks/useTheme'

interface NavigationItem {
  name: string
  href: string
  icon: typeof Home
}

interface PageLayoutProps {
  children: ReactNode
}

const STORAGE_KEY = 'sidebar-collapsed'

/**
 * PageLayout — app shell with left sidebar navigation. Only rendered for authenticated users.
 */
export default function PageLayout({ children }: PageLayoutProps) {
  const location = useLocation()
  const { user, logout } = useAuthContext()
  const { preference, toggle } = useTheme()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true'
    } catch {
      return false
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(collapsed))
    } catch { /* localStorage may be unavailable */ }
  }, [collapsed])

  const navigation: NavigationItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Lista ogłoszeń', href: '/notices', icon: FileText },
    { name: 'Otwarte przetargi', href: '/open-tenders', icon: Briefcase },
    { name: 'Zamawiający', href: '/organizations', icon: Building2 },
    { name: 'Wykonawcy', href: '/contractors', icon: Award },
    { name: 'Statystyki', href: '/statistics', icon: BarChart3 },
  ]

  return (
    <div className="min-h-screen flex bg-surface">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? 'w-14' : 'w-[200px]'} flex-shrink-0 bg-surface-raised border-r border-border-subtle flex flex-col transition-[width] duration-200`}
      >
        {/* Logo */}
        <div
          className={`${collapsed ? 'px-0 text-center' : 'px-4'} pb-3 mb-2 border-b border-border-subtle`}
        >
          <Link to="/dashboard" className="inline-flex items-center gap-2 pt-4">
            <Landmark className={`${collapsed ? 'h-5 w-5' : 'h-5 w-5'} text-ink flex-shrink-0`} />
            {!collapsed && (
              <span className="text-lg font-bold tracking-tight text-ink">
                Bid<span className="text-brand">Desk</span>
              </span>
            )}
          </Link>
        </div>

        {/* Nav items */}
        <nav className="flex-1 py-1">
          {navigation.map((item) => {
            const Icon = item.icon
            const isActive =
              location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href))

            return (
              <Link
                key={item.name}
                to={item.href}
                title={collapsed ? item.name : undefined}
                className={`flex items-center gap-2.5 mx-1.5 my-0.5 rounded-md transition-colors duration-100 ${
                  collapsed ? 'justify-center px-0 py-2' : 'px-4 py-2'
                } ${
                  isActive
                    ? 'border-l-2 border-brand bg-brand-bg text-ink font-semibold'
                    : 'border-l-2 border-transparent text-ink-secondary hover:bg-surface-hover hover:text-ink'
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                {!collapsed && <span className="text-xs">{item.name}</span>}
              </Link>
            )
          })}
        </nav>

        {/* User + Logout */}
        <div className="border-t border-border-subtle px-3 py-3">
          {!collapsed && (
            <div className="flex items-center gap-2 mb-2">
              <User className="h-3.5 w-3.5 text-ink-muted flex-shrink-0" />
              <span className="text-xs text-ink-secondary truncate">
                {user?.name || user?.email}
              </span>
            </div>
          )}
          <button
            onClick={logout}
            title="Wyloguj"
            className={`flex items-center gap-2 text-xs text-ink-muted hover:text-ink transition-colors ${collapsed ? 'justify-center w-full' : ''}`}
          >
            <LogOut className="h-3.5 w-3.5" />
            {!collapsed && <span>Wyloguj</span>}
          </button>
        </div>

        {/* Theme toggle */}
        <button
          onClick={toggle}
          title={preference === 'light' ? 'Tryb jasny' : preference === 'dark' ? 'Tryb ciemny' : 'Tryb systemowy'}
          className={`flex items-center gap-2 text-xs text-ink-muted hover:text-ink transition-colors border-t border-border-subtle w-full py-2 ${collapsed ? 'justify-center' : 'px-3'}`}
        >
          {preference === 'light' && <Sun className="h-3.5 w-3.5 flex-shrink-0" />}
          {preference === 'dark' && <Moon className="h-3.5 w-3.5 flex-shrink-0" />}
          {preference === 'system' && <Monitor className="h-3.5 w-3.5 flex-shrink-0" />}
          {!collapsed && (
            <span>{preference === 'light' ? 'Jasny' : preference === 'dark' ? 'Ciemny' : 'System'}</span>
          )}
        </button>

        {/* Collapse toggle */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="border-t border-border-subtle py-2 text-ink-faint hover:text-ink-muted transition-colors flex items-center justify-center"
        >
          {collapsed ? (
            <ChevronsRight className="h-4 w-4" />
          ) : (
            <ChevronsLeft className="h-4 w-4" />
          )}
        </button>
      </aside>

      {/* Main content */}
      <main className="flex-1 min-w-0 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl">{children}</main>
    </div>
  )
}

interface PageHeaderProps {
  title: string
  description?: string
  actions?: ReactNode
}

/**
 * PageHeader component for consistent page titles and descriptions
 */
export function PageHeader({ title, description, actions }: PageHeaderProps) {
  return (
    <div className="mb-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-3xl font-bold text-ink">{title}</h2>
          {description && <p className="mt-1 text-sm text-ink-secondary">{description}</p>}
        </div>
        {actions && <div className="flex space-x-3">{actions}</div>}
      </div>
    </div>
  )
}

interface BackButtonProps {
  to: string
  children: ReactNode
  [key: string]: any
}

/**
 * BackButton component for navigation
 */
export function BackButton({ to, children, ...props }: BackButtonProps) {
  return (
    <Link
      to={to}
      className="inline-flex items-center px-4 py-2 border border-border rounded-md text-sm font-medium text-ink-secondary bg-surface-raised hover:bg-surface-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand"
      {...props}
    >
      {children}
    </Link>
  )
}
